import { usePdfMaker } from '@/utils/pdf/pdf';
import type {
  Content,
  ContentImage,
  ContentText,
  TDocumentDefinitions,
} from 'pdfmake/interfaces';
import type { Participant } from '@/components/seminar/Participant/types';
import type { ParticularSeminar } from '@/components/seminar/SeminarTable/types';
import { formatDate } from 'date-formatter';
import { SeminarType } from '@/api/graphql/codegen/graphql';
import JSZip from 'jszip';

export const useGenerateCertificates = () => {
  const { generatePdf, generatePdfBlob } = usePdfMaker();

  const getDocumentDefinition = (
    content: Content | Content[],
  ): TDocumentDefinitions => ({
    content: Array.isArray(content) ? content : [content],
    images: {
      vysEduLogo:
        'https://vys-edu.s3.eu-central-1.amazonaws.com/vys-edu-logo.jpeg',
      euMsmtLogo:
        'https://vys-edu.s3.eu-central-1.amazonaws.com/eu-msmt-pdfmake.png',
      eu: 'https://vys-edu.s3.eu-central-1.amazonaws.com/eu.jpg?pdfmake=true',
      planNarodniObnovy:
        'https://vys-edu.s3.eu-central-1.amazonaws.com/plan_narodni_obnovy.jpg?pdfmake=true',
      msmt: 'https://vys-edu.s3.eu-central-1.amazonaws.com/msmt.jpg?pdfmake=true',
    },
    styles: {
      default: {
        alignment: 'center',
      },
      subheader: {
        fontSize: 13,
        bold: true,
      },
      title: {
        fontSize: 36,
        alignment: 'center',
        bold: true,
        marginTop: 30,
        marginBottom: 30,
      },
      subtitle: {
        fontSize: 20,
        alignment: 'center',
        bold: true,
        marginTop: 20,
        marginBottom: 20,
      },
    },
    info: {
      title: 'Osvědčení',
      author: 'Vysočina Education',
      subject: 'Osvědčení',
      keywords: 'Osvědčení',
    },
  });

  const generateCertificates = async (
    seminar: ParticularSeminar,
    selectedParticipants?: Participant[],
  ) => {
    const participants =
      selectedParticipants ||
      seminar.participants?.filter((p) => !p.isSubstitute);
    if (!participants || participants.length === 0 || !seminar.participants)
      return;

    // If selectedParticipants is provided, generate individual PDFs and create a ZIP
    if (selectedParticipants && selectedParticipants.length > 1) {
      await generateSeparatedCertificatesAndDownloadAsZip({
        seminar,
        allParticipants: seminar.participants,
        selectedParticipants,
      });
    } else {
      generatePdfCertificates({
        seminar,
        allParticipants: seminar.participants,
        selectedParticipants: participants,
      });
    }
  };

  const createCertificateForParticipant = (
    seminar: ParticularSeminar,
    participant: Participant,
    participants: Participant[],
    // identifier: number,
    addPageBreak = false,
  ) => {
    const identifier =
      participants
        .filter((p) => !p.isSubstitute)
        .findIndex((p) => p.id === participant.id) + 1;

    let images: Content[] = [];
    if (seminar.type === SeminarType.Idz) {
      images = [
        {
          image: 'euMsmtLogo',
          alignment: 'center',
          width: 5581 / 18,
          height: 795 / 18,
          margin: [0, 0, 0, 15],
        } as ContentImage,
      ];
    } else if (seminar.type === SeminarType.Digcompedu) {
      images = [
        {
          columns: [
            {
              image: 'eu',
              alignment: 'center',
              width: 565 / 5,
              height: 150 / 5,
              margin: [15, 0],
            } as ContentImage,
            {
              image: 'planNarodniObnovy',
              alignment: 'center',
              width: 1667 / 18,
              height: 512 / 18,
              margin: [15, 0],
            } as ContentImage,
            {
              image: 'msmt',
              alignment: 'center',
              width: 827 / 13,
              height: 413 / 13,
              margin: [15, 0],
            } as ContentImage,
          ],
        },
      ];
    }

    const certificate: Content = [
      {
        alignment: 'center',
        fontSize: 9,
        columns: [
          [
            ...images,

            {
              text: 'VYSOČINA EDUCATION',
              style: 'subheader',
            },
            'školské zařízení pro další vzdělávání pedagogických pracovníků',
            'příspěvková organizace IČO: 751 40 349',
            {
              text: 'akreditovaná pod č.j.: 1122/2021-1',
              style: 'subheader',
            },
            {
              text: 'Ke Skalce 5907/47, 586 01 Jihlava',
              style: 'subheader',
            },
          ],
          ...(seminar.type === SeminarType.Default ||
          seminar.type === SeminarType.Studium
            ? [
                {
                  image: 'vysEduLogo',
                  width: 100,
                  height: 100,
                  margin: [5, -10, 5, 0],
                } as ContentImage,
              ]
            : []),
        ],
      },
      {
        table: {
          widths: ['*'],
          body: [[' '], [' ']],
        } as any,
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? 0 : 1;
          },
          vLineWidth: function () {
            return 0;
          },
        },
        marginTop: -15,
      },
      {
        text: `${seminar.identifier} - ${identifier}`,
        alignment: 'left',
        marginTop: -15,
      },
      {
        text: 'OSVĚDČENÍ',
        style: 'title',
        margin: seminar.type === SeminarType.Idz ? 5 : 20,
      },
      {
        text: participant.name,
        style: 'subtitle',
      },
      {
        text: `narozen(a) ${formatDate(participant.dateOfBirth)}`,
        style: 'default',
      },
      ...(seminar.type === SeminarType.Studium
        ? [
            {
              text: `místo narození: ${participant.placeOfBirth ?? ''}`,
              style: 'default',
            } as ContentText,
          ]
        : []),
      {
        text: `absolvoval(a) seminář`,
        style: 'default',
      },
      {
        text: seminar.name,
        style: 'subtitle',
        marginBottom: 40,
      },
      {
        text: seminar.accreditation
          ? `Vzdělávací program byl akreditován MŠMT ČR v rámci dalšího vzdělávání pedagogických pracovníků pod č. j. ${seminar.accreditation}`
          : '',
        marginBottom: seminar.type === SeminarType.Idz ? 10 : 40,
        style: {
          alignment: 'center',
        },
      },
      ...(seminar.type === SeminarType.Idz
        ? [
            {
              text:
                'Implementace dlouhodobého záměru Kraje Vysočina.\n' +
                'CZ.02.02.XX/00/23_018/0010144 \n' +
                'Tento projekt je spolufinancován Evropskou unií.',
              marginBottom: 10,
              style: {
                alignment: 'center',
              },
            } as ContentText,
          ]
        : []),
      {
        stack: [
          `datum konání: ${formatDate(seminar.date)}${
            seminar.endDate ? ` - ${formatDate(seminar.endDate)}` : ''
          }`,
          `místo: ${seminar.city}`,
          ...(seminar.type === SeminarType.Studium
            ? [
                `počet hodin teorie: ${seminar.theoryDuration}`,
                ` počet hodin praxe: ${seminar.practiceDuration}`,
              ]
            : [`počet hodin: ${seminar.duration}`]),
          seminar.lector ? `lektor: ${seminar.lector}` : '',
        ],
        alignment: 'center',
        margin:
          seminar.type === SeminarType.Default
            ? [0, 50, 0, 50]
            : [0, 30, 0, 30],
      },
      ...(seminar.type === SeminarType.Studium
        ? []
        : [
            {
              text: `Způsob zakončení programu: Účastníci vyplnili evaluační dotazník a obdrželi osvědčení.`,
              alignment: 'center',
            } as ContentText,
          ]),
      {
        columns: [
          {
            stack: [
              seminar.city,
              `${formatDate(seminar.endDate ?? seminar.date)}`,
            ],
            alignment: 'left',
          },
          {
            text: 'razítko',
            alignment: 'center',
            marginRight: 40,
          },
          {
            stack: [
              `v z. ..........................................`,
              'Mgr. Roman Křivánek',
              'Ředitel',
            ],
            alignment: 'center',
          },
        ],
        alignment: 'justify',
        absolutePosition: { x: 80, y: 730 },
      },
      {
        ...(addPageBreak
          ? {
              text: '',
              pageBreak: 'after',
            }
          : { text: '' }),
      },
    ];

    return certificate;
  };

  const generatePdfCertificates = ({
    seminar,
    allParticipants,
    selectedParticipants,
  }: {
    seminar: ParticularSeminar;
    allParticipants: Participant[];
    selectedParticipants: Participant[];
  }) => {
    const certificates = selectedParticipants.map((participant, index) =>
      createCertificateForParticipant(
        seminar,
        participant,
        allParticipants,
        index !== selectedParticipants.length - 1,
      ),
    );

    const docDefinition = getDocumentDefinition(certificates);

    const fileName =
      selectedParticipants.length === 1
        ? `${selectedParticipants[0].name} - Osvědčení - ${formatDate(
            seminar.date,
          )}`
        : `Osvědčení - ${formatDate(seminar.date)}`;

    generatePdf(docDefinition, fileName);
  };

  const generateSeparatedCertificatesAndDownloadAsZip = async ({
    seminar,
    allParticipants,
    selectedParticipants,
  }: {
    seminar: ParticularSeminar;
    allParticipants: Participant[];
    selectedParticipants: Participant[];
  }) => {
    const zip = new JSZip();

    // Create individual PDF for each participant
    for (let i = 0; i < selectedParticipants.length; i++) {
      const participant = selectedParticipants[i];
      const certificate = createCertificateForParticipant(
        seminar,
        participant,
        allParticipants,
        false,
      );

      const docDefinition = getDocumentDefinition(certificate);

      // Generate PDF blob for this participant
      const pdfBlob = await generatePdfBlob(docDefinition);

      // Add to ZIP with participant name as filename
      const fileName = `${participant.name} - Osvědčení - ${formatDate(
        seminar.date,
      )}.pdf`;
      zip.file(fileName, pdfBlob);
    }

    // Generate and download ZIP file
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Osvědčení - ${formatDate(seminar.date)}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    generateCertificates,
  };
};
